import React, { useState, useCallback, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CircularProgress, Dialog, DialogContent, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { useMutation } from '@apollo/client';
import { greens, greys, onwardNavy } from '@/styles/colors';
import { UPDATE_ORDER } from '@/components/OrderDetailsPage/mutations';
import { auditTitle, routeMarginDollar } from './utils';
import Sidebar from './Sidebar';
import RouteOverview from './RouteOverview';
import OrderView from './OrderView';

// ---- Shared button styles ----
const BtnBase = styled.button`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 14px;
    padding: 10px 22px;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.15s;
`;
const GhostBtn = styled(BtnBase)`
    background: transparent;
    border: 1px solid transparent;
    color: ${greys[2]};
    &:hover {
        background: #f0f0f0;
    }
`;
const PrimaryBtn = styled(BtnBase)`
    background: ${({ disabled }) => (disabled ? '#bcdcc1' : greens[0])};
    border: none;
    color: white;
    &:hover {
        background: ${({ disabled }) => (disabled ? '#bcdcc1' : '#187522')};
    }
`;
const SecondaryBtn = styled(BtnBase)`
    background: white;
    border: 1px solid ${greys[4]};
    color: ${greys[3]};
    &:hover {
        background: #f7f7f7;
    }
`;
const GoldBtn = styled(BtnBase)`
    background: white;
    border: 2px solid #e28700;
    color: #e28700;
    &:hover {
        background: #fbf1e0;
    }
`;

// Seed the modal's local audit state from the orders' persisted review flag so a
// previously-reviewed order shows as audited when the modal (re)opens.
const seedAuditStates = (orders) =>
    Object.fromEntries(
        (orders || [])
            .filter((o) => o.invoicing_status === 'PRE_APPROVED')
            .map((o) => [o.order_id, { audited: true, skipped: false }])
    );

export default function AccessorialAuditModal({
    open,
    mode = 'route',
    route,
    orders,
    onClose,
    onApprove,
    onApproveOne,
    canEditPerspective,
    showAllBreakdowns = false,
}) {
    const isQueue = mode === 'queue';
    // Local working list. In route mode this never changes (mirrors `orders`); in
    // queue mode orders are removed as they are approved/skipped so they fall off.
    const [queue, setQueue] = useState(() => orders || []);
    // Queue mode opens straight on the first order; route mode opens on the overview.
    const [screen, setScreen] = useState(() => (mode === 'queue' && (orders || []).length ? 0 : 'overview'));
    const [auditStates, setAuditStates] = useState(() => seedAuditStates(orders));
    const [orderClones, setOrderClones] = useState(() =>
        Object.fromEntries((orders || []).map((o) => [o.order_id, o]))
    );
    const [saving, setSaving] = useState(false);

    const [updateOrder] = useMutation(UPDATE_ORDER, { onError: console.error });

    // flushRef is set by the mounted OrderView so approveAndNext can trigger a save
    const flushRef = useRef(null);

    // Local-only update — keeps orderClones in sync for live margin display.
    // Does NOT fire a mutation; the mutation fires via flushAndSave (on Approve).
    const modifyOrder = useCallback((orderId, changes) => {
        setOrderClones((prev) => ({ ...prev, [orderId]: { ...prev[orderId], ...changes } }));
    }, []);

    // Fires the actual mutation. Called by OrderView's flushRef on Approve,
    // and also wired as the shim's modifyOrder for explicit "Save Charges" clicks.
    const flushAndSave = useCallback(
        async (orderId, changes) => {
            setOrderClones((prev) => ({ ...prev, [orderId]: { ...prev[orderId], ...changes } }));
            try {
                await updateOrder({ variables: { order_id: orderId, _set: changes } });
            } catch {
                setOrderClones((prev) => ({
                    ...prev,
                    [orderId]: (orders || []).find((o) => o.order_id === orderId),
                }));
            }
        },
        [orders, updateOrder]
    );

    // Persist the "reviewed" flag so an audit decision survives closing the modal
    // and drives the checkmark on the Pending Approval table. Approve sets it true;
    // Skip sets it false.
    const markReviewed = useCallback(
        (orderId, reviewed) =>
            updateOrder({
                variables: {
                    order_id: orderId,
                    _set: { invoicing_status: reviewed ? 'PRE_APPROVED' : 'NOT_APPROVED' },
                },
            }),
        [updateOrder]
    );

    const progressAuditing = useCallback(() => {
        const idx = queue.findIndex((o) => !auditStates[o.order_id]?.audited && !auditStates[o.order_id]?.skipped);
        setScreen(idx >= 0 ? idx : 0);
    }, [queue, auditStates]);

    const handleClose = useCallback(() => {
        if (saving) return;
        setScreen('overview');
        setAuditStates(seedAuditStates(orders));
        setQueue(orders || []);
        setOrderClones(Object.fromEntries((orders || []).map((o) => [o.order_id, o])));
        onClose();
    }, [saving, orders, onClose]);

    const approveAndNext = useCallback(async () => {
        const idx = typeof screen === 'number' ? screen : 0;
        const order = queue[idx];
        if (!order) return;

        setSaving(true);
        try {
            if (flushRef.current) {
                await flushRef.current();
            }

            if (isQueue) {
                // Persist the review (invoicing_status → PRE_APPROVED) without approving
                // for invoicing — the batch "Approve Reviewed Orders" action handles that.
                const ok = onApproveOne(order.order_id);
                // if (!ok) return;

                setAuditStates((prev) => ({ ...prev, [order.order_id]: { audited: true, skipped: false } }));
                setPendingLiveOrder(null);
                const next = idx + 1;
                if (next >= queue.length) {
                    handleClose();
                } else {
                    setScreen(next);
                }
                return;
            }

            // Route mode: persist the review, mark audited locally, and advance;
            // batch-approve (ready_to_invoice) happens later on the overview.
            await markReviewed(order.order_id, true);
            setAuditStates((prev) => ({ ...prev, [order.order_id]: { audited: true, skipped: false } }));
            const next = idx + 1;
            setPendingLiveOrder(null);
            setScreen(next >= queue.length ? 'overview' : next);
        } finally {
            setSaving(false);
        }
    }, [screen, queue, isQueue, onApproveOne, handleClose, markReviewed]);

    const skipOrder = useCallback(() => {
        const idx = typeof screen === 'number' ? screen : 0;
        const order = queue[idx];
        if (!order) return;

        // Skipping records the order as reviewed=false so it stays unchecked in the table.
        markReviewed(order.order_id, false);

        if (isQueue) {
            // Skip records reviewed=false but keeps the order in the queue, marked skipped.
            setAuditStates((prev) => ({ ...prev, [order.order_id]: { audited: false, skipped: true } }));
            setPendingLiveOrder(null);
            const next = idx + 1;
            if (next >= queue.length) {
                handleClose();
            } else {
                setScreen(next);
            }
            return;
        }

        setAuditStates((prev) => ({ ...prev, [order.order_id]: { audited: false, skipped: true } }));
        const next = idx + 1;
        setPendingLiveOrder(null);
        setScreen(next >= queue.length ? 'overview' : next);
    }, [screen, queue, isQueue, handleClose, markReviewed]);

    const handleApprove = useCallback(
        (which) => {
            const ids =
                which === 'all'
                    ? queue.map((o) => o.order_id)
                    : queue.filter((o) => auditStates[o.order_id]?.audited).map((o) => o.order_id);
            onApprove(ids);
            handleClose();
        },
        [queue, auditStates, onApprove, handleClose]
    );

    const isOrder = typeof screen === 'number';
    const currentOrder = isOrder ? queue[screen] : null;
    const clonedOrder = currentOrder ? orderClones[currentOrder.order_id] || currentOrder : null;

    const allClonedOrders = useMemo(() => queue.map((o) => orderClones[o.order_id] || o), [queue, orderClones]);
    // Baseline reflects the original full route, so it stays sourced from `orders`.
    const baselineRouteMargin = useMemo(() => routeMarginDollar(orders || []), [orders]);

    // Live (pre-save) version of the currently viewed order, reported back by OrderView
    const [pendingLiveOrder, setPendingLiveOrder] = useState(null);

    // Sidebar orders: replace the active order with its live version while editing
    const sidebarOrders = useMemo(() => {
        if (!isOrder || !pendingLiveOrder || !currentOrder) return allClonedOrders;
        return allClonedOrders.map((o) => (o.order_id === currentOrder.order_id ? pendingLiveOrder : o));
    }, [allClonedOrders, isOrder, pendingLiveOrder, currentOrder]);

    const auditedCount = queue.filter((o) => auditStates[o.order_id]?.audited).length;
    const totalCount = queue.length;
    const allAudited = auditedCount === totalCount && totalCount > 0;
    const someAudited = auditedCount > 0 && !allAudited;
    const isLast = isOrder && screen === totalCount - 1;

    const routeName = useMemo(() => auditTitle({ isQueue, route }), [isQueue, route]);
    const routeSubname = useMemo(() => {
        const first = queue[0];
        const shipper = first?.order_shipper?.business_name;
        const carrier = first?.order_carrier?.business_name;
        return [shipper, carrier].filter(Boolean).join(' · ');
    }, [queue]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            disableBackdropClick={saving}
            disableEscapeKeyDown={saving}
            PaperProps={{
                style: {
                    borderRadius: 14,
                    boxShadow: '0 24px 70px rgba(12,11,29,0.4)',
                    height: 'min(880px, calc(100vh - 48px))',
                    maxWidth: 1480,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    padding: 14px 22px;
                    border-bottom: 1px solid ${greys[4]};
                    background: white;
                    flex-shrink: 0;
                `}
            >
                <div
                    css={css`
                        flex: 1;
                    `}
                >
                    <div
                        css={css`
                            font-family: Montserrat, Roboto, Arial, sans-serif;
                            font-weight: 800;
                            font-size: 17px;
                            color: ${onwardNavy};
                        `}
                    >
                        {routeName}
                    </div>
                    {routeSubname && (
                        <div
                            css={css`
                                font-family: Montserrat, Roboto, Arial, sans-serif;
                                font-weight: 600;
                                font-size: 12px;
                                color: ${greys[1]};
                                margin-top: 2px;
                            `}
                        >
                            {routeSubname}
                        </div>
                    )}
                </div>
                <IconButton size="small" onClick={handleClose} disabled={saving}>
                    <CloseIcon
                        css={css`
                            color: ${greys[2]};
                        `}
                    />
                </IconButton>
            </div>

            {/* Body: sidebar + scrollable content, side by side */}
            <DialogContent
                css={css`
                    padding: 0;
                    display: flex;
                    flex-direction: row;
                    flex: 1;
                    min-height: 0;
                    overflow: hidden;
                `}
            >
                <Sidebar
                    mode={mode}
                    route={route}
                    orders={sidebarOrders}
                    screen={screen}
                    auditStates={auditStates}
                    canEditPerspective={canEditPerspective}
                    onNavigate={(idx) => {
                        setPendingLiveOrder(null);
                        setScreen(idx);
                    }}
                />
                <div
                    css={css`
                        flex: 1;
                        min-width: 0;
                        background: white;
                        display: flex;
                        overflow: hidden;
                    `}
                >
                    {isOrder && clonedOrder ? (
                        <OrderView
                            key={currentOrder.order_id}
                            order={currentOrder}
                            clonedOrder={clonedOrder}
                            orderIndex={screen}
                            totalOrders={totalCount}
                            onModifyOrder={modifyOrder}
                            onFlushSave={flushAndSave}
                            flushRef={flushRef}
                            allClonedOrders={allClonedOrders}
                            baselineRouteMargin={baselineRouteMargin}
                            onLiveOrderChange={setPendingLiveOrder}
                            canEditPerspective={canEditPerspective}
                            showAllBreakdowns={showAllBreakdowns}
                            showRouteMargin={!isQueue}
                        />
                    ) : (
                        <div
                            css={css`
                                flex: 1;
                                overflow-y: auto;
                                min-width: 0;
                            `}
                        >
                            <RouteOverview
                                orders={orders || []}
                                orderClones={orderClones}
                                auditStates={auditStates}
                                onGoToOrder={setScreen}
                                canEditPerspective={canEditPerspective}
                            />
                        </div>
                    )}
                </div>
            </DialogContent>

            {/* Footer */}
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 22px;
                    border-top: 1px solid ${greys[4]};
                    background: #fafafa;
                    flex-shrink: 0;
                `}
            >
                <GhostBtn onClick={handleClose} disabled={saving}>
                    Cancel
                </GhostBtn>

                <div
                    css={css`
                        display: flex;
                        gap: 12px;
                        align-items: center;
                    `}
                >
                    {isQueue ? (
                        <>
                            <GoldBtn onClick={skipOrder} disabled={saving}>
                                Skip
                            </GoldBtn>
                            <PrimaryBtn onClick={approveAndNext} disabled={saving}>
                                {saving ? (
                                    <CircularProgress size={16} style={{ color: 'white' }} />
                                ) : isLast ? (
                                    <>
                                        <CheckIcon style={{ fontSize: 16 }} />
                                        &nbsp;Approve &amp; Finish
                                    </>
                                ) : (
                                    <>Approve &amp; Next →</>
                                )}
                            </PrimaryBtn>
                        </>
                    ) : isOrder ? (
                        <>
                            <GoldBtn onClick={skipOrder} disabled={saving}>
                                Skip
                            </GoldBtn>
                            <PrimaryBtn onClick={approveAndNext} disabled={saving}>
                                {saving ? (
                                    <CircularProgress size={16} style={{ color: 'white' }} />
                                ) : isLast ? (
                                    <>
                                        <CheckIcon style={{ fontSize: 16 }} />
                                        &nbsp;Finish &amp; Review Route
                                    </>
                                ) : (
                                    <>Approve &amp; Next Order →</>
                                )}
                            </PrimaryBtn>
                        </>
                    ) : allAudited ? (
                        <PrimaryBtn onClick={() => handleApprove('all')}>
                            <CheckIcon style={{ fontSize: 16 }} />
                            &nbsp;Approve All Orders
                        </PrimaryBtn>
                    ) : someAudited ? (
                        <>
                            <SecondaryBtn onClick={progressAuditing}>Continue Auditing</SecondaryBtn>
                            <PrimaryBtn onClick={() => handleApprove('audited')}>
                                <CheckIcon style={{ fontSize: 16 }} />
                                &nbsp;Approve {auditedCount} Audited Order{auditedCount !== 1 ? 's' : ''}
                            </PrimaryBtn>
                        </>
                    ) : (
                        <PrimaryBtn onClick={progressAuditing}>Begin Auditing →</PrimaryBtn>
                    )}
                </div>
            </div>
        </Dialog>
    );
}
